import Image from 'next/image'
import { motion } from 'framer-motion'

export interface FeatureCardProps {
  image: string
  imageAlt: string
  title: string
  description: string
  icon?: React.ReactNode
}

interface FeatureCardsProps {
  title: string
  titleHighlight?: string
  features: FeatureCardProps[]
  className?: string
}

export default function FeatureCards({
  title,
  titleHighlight,
  features,
  className = '',
}: FeatureCardsProps) {
  return (
    <div className={`pt-5 pb-20 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mx-auto max-w-7xl"
      >
        <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
          {title} {titleHighlight && <span className="text-[#ff4500]">{titleHighlight}</span>}
        </h2>
        <div className="grid gap-8 md:grid-cols-3">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 * index }}
              className="rounded-xl bg-gray-50 p-6 shadow-lg transition-transform hover:scale-105 dark:bg-gray-800"
            >
              <div className="relative mb-6 h-48 w-full overflow-hidden rounded-lg">
                <Image
                  src={feature.image}
                  alt={feature.imageAlt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
                />
              </div>

              {feature.icon && (
                <div className="mb-4 flex justify-center md:justify-start">
                  <div className="mb-4 h-12 w-12 rounded-full bg-[#ff4500] p-3">{feature.icon}</div>
                </div>
              )}

              <h3 className="mb-2 text-center text-xl font-bold md:text-left">{feature.title}</h3>
              <p className="text-center text-gray-600 md:text-left dark:text-gray-300">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  )
}
